import { useCallback, useRef, useState } from 'react';
import deepgramService from '../services/deepgram.service';

export type DeepgramConnectionStatus = 'idle' | 'connecting' | 'live' | 'error' | 'closed';

// A run of contiguous speech attributed to one speaker. `speaker` is Deepgram's
// 0-based diarization index; the UI renders it as `Speaker ${speaker + 1}`.
export type TranscriptSegment = {
  speaker: number;
  text: string;
};

type DeepgramWord = {
  word?: string;
  punctuated_word?: string;
  speaker?: number;
};

// Group consecutive words by speaker into labeled runs. Uses punctuated_word
// (from smart_format) when present so the text reads naturally.
const wordsToSegments = (words: DeepgramWord[]): TranscriptSegment[] => {
  const segments: TranscriptSegment[] = [];
  for (const word of words || []) {
    const text = word.punctuated_word || word.word || '';
    if (!text) {
      continue;
    }
    const speaker = typeof word.speaker === 'number' ? word.speaker : 0;
    const last = segments[segments.length - 1];
    if (last && last.speaker === speaker) {
      last.text = `${last.text} ${text}`;
    } else {
      segments.push({ speaker, text });
    }
  }
  return segments;
};

type Options = {
  clinicId?: number;
  // MediaRecorder MIME type — must match the container Deepgram will decode.
  // Deepgram's live endpoint reliably handles webm/opus; mp4 (iOS/Safari)
  // chunks are not individually decodable, so live streaming is skipped there
  // and the backend batch path transcribes the uploaded file instead.
  mimeType: string;
  onError?: (message: string) => void;
};

const DEEPGRAM_WS_BASE = 'wss://api.deepgram.com/v1/listen';

/**
 * Streams microphone audio straight from the browser to Deepgram for live
 * transcription. The caller passes the same MediaStream WaveSurfer is recording
 * (`record.stream`); we attach our own MediaRecorder so the existing recording
 * flow is untouched. The accumulated final transcript is returned from stop()
 * and persisted downstream (reused so the backend skips re-transcription).
 */
export const useDeepgramLiveTranscription = ({ clinicId, mimeType, onError }: Options) => {
  const [status, setStatus] = useState<DeepgramConnectionStatus>('idle');
  // Finalized, speaker-labeled segments — this is what we persist.
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  // Interim (in-progress) segments for the current utterance — replaced as
  // Deepgram refines it, cleared once finalized.
  const [interimSegments, setInterimSegments] = useState<TranscriptSegment[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  // Synchronous mirror of `segments` so stop() can build the transcript without
  // waiting for a re-render.
  const segmentsRef = useRef<TranscriptSegment[]>([]);
  const keepAliveRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // True once we've intentionally torn down, so error/close handlers stay quiet.
  const closingRef = useRef(false);

  const isSupported = useCallback(() => {
    return (
      typeof MediaRecorder !== 'undefined' &&
      typeof MediaRecorder.isTypeSupported === 'function' &&
      MediaRecorder.isTypeSupported(mimeType) &&
      // mp4 chunks can't be streamed incrementally — fall back to batch.
      !mimeType.includes('mp4')
    );
  }, [mimeType]);

  const clearKeepAlive = useCallback(() => {
    if (keepAliveRef.current) {
      clearInterval(keepAliveRef.current);
      keepAliveRef.current = null;
    }
  }, []);

  const teardown = useCallback(() => {
    clearKeepAlive();

    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      try {
        recorder.stop();
      } catch {
        // ignore
      }
    }
    recorderRef.current = null;

    const ws = wsRef.current;
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try {
        ws.close();
      } catch {
        // ignore
      }
    }
    wsRef.current = null;
  }, [clearKeepAlive]);

  const reset = useCallback(() => {
    segmentsRef.current = [];
    setSegments([]);
    setInterimSegments([]);
    setStatus('idle');
  }, []);

  /**
   * Open the Deepgram WebSocket for `stream` and start feeding audio.
   * No-op (and returns false) when live streaming isn't supported.
   */
  const connect = useCallback(
    async (stream: MediaStream): Promise<boolean> => {
      if (!clinicId || !isSupported()) {
        return false;
      }

      closingRef.current = false;
      setStatus('connecting');

      // Start capturing immediately — before the token fetch and WS handshake —
      // so the first words aren't lost. Chunks buffer until the socket opens,
      // then flush in order.
      const pendingChunks: Blob[] = [];
      let socketOpen = false;

      let recorder: MediaRecorder;
      try {
        recorder = new MediaRecorder(stream, { mimeType });
      } catch {
        setStatus('error');
        onError?.('Could not start live transcription.');
        return false;
      }
      recorderRef.current = recorder;
      recorder.ondataavailable = event => {
        if (!event.data || event.data.size === 0) {
          return;
        }
        const ws = wsRef.current;
        if (socketOpen && ws && ws.readyState === WebSocket.OPEN) {
          ws.send(event.data);
        } else {
          pendingChunks.push(event.data);
        }
      };
      // 250ms chunks balance latency against per-message overhead.
      recorder.start(250);

      let token: string;
      try {
        const res = await deepgramService.getToken(clinicId);
        token = res?.accessToken;
        if (!token) {
          throw new Error('No Deepgram token returned');
        }
      } catch {
        setStatus('error');
        onError?.('Could not start live transcription.');
        teardown();
        return false;
      }

      // Query params shape the live transcription. interim_results streams
      // partial words so the UI updates as the provider speaks. diarize adds a
      // per-word `speaker` index so we can label "Speaker 1 / Speaker 2" live.
      //
      // Multilingual mode captures English questions and Spanish responses in
      // the same recording before the subjective summary is generated.
      const params = new URLSearchParams({
        model: 'nova-3',
        smart_format: 'true',
        language: 'multi',
        interim_results: 'true',
        punctuate: 'true',
        diarize: 'true',
      });

      // Browsers can't set Authorization headers on a WebSocket; Deepgram reads
      // auth from the Sec-WebSocket-Protocol subprotocols. A temporary token
      // (JWT from /auth/grant) uses the "bearer" scheme — NOT "token", which is
      // only for raw API keys. (Deepgram SDK CustomClient.getWebSocketOptions.)
      const ws = new WebSocket(`${DEEPGRAM_WS_BASE}?${params.toString()}`, ['bearer', token]);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('live');
        socketOpen = true;

        // Flush everything captured during the handshake, then stream live.
        for (const chunk of pendingChunks) {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(chunk);
          }
        }
        pendingChunks.length = 0;

        // KeepAlive prevents Deepgram from closing the socket during silences.
        keepAliveRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'KeepAlive' }));
          }
        }, 8000);
      };

      ws.onmessage = event => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type !== 'Results') {
            return;
          }
          // With diarize=true each word carries a `speaker` index. The flat
          // `transcript` string is unlabeled, so we build speaker runs from words.
          const words: DeepgramWord[] = msg.channel?.alternatives?.[0]?.words || [];
          const runs = wordsToSegments(words);

          if (msg.is_final) {
            if (runs.length === 0) {
              return;
            }
            // Append finalized runs, merging into the previous segment when the
            // same speaker continues so each speaker reads as one paragraph.
            const merged = segmentsRef.current.map(s => ({ ...s }));
            for (const run of runs) {
              const last = merged[merged.length - 1];
              if (last && last.speaker === run.speaker) {
                last.text = `${last.text} ${run.text}`.trim();
              } else {
                merged.push({ speaker: run.speaker, text: run.text });
              }
            }
            segmentsRef.current = merged;
            setSegments(merged);
            setInterimSegments([]);
          } else {
            setInterimSegments(runs);
          }
        } catch {
          // Non-JSON / unexpected frame — ignore.
        }
      };

      ws.onerror = () => {
        if (!closingRef.current) {
          setStatus('error');
        }
      };

      ws.onclose = () => {
        clearKeepAlive();
        if (!closingRef.current) {
          setStatus('closed');
        }
      };

      return true;
    },
    [clinicId, isSupported, mimeType, onError, clearKeepAlive]
  );

  // Pause feeding audio (recording paused). The socket stays open via KeepAlive.
  const pause = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'recording') {
      try {
        recorder.pause();
      } catch {
        // ignore
      }
    }
  }, []);

  const resume = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === 'paused') {
      try {
        recorder.resume();
      } catch {
        // ignore
      }
    }
  }, []);

  /**
   * Stop streaming, flush Deepgram's buffered audio, and resolve with the
   * accumulated final transcript. Waits briefly for trailing final results so
   * the last spoken words aren't dropped.
   */
  const stop = useCallback((): Promise<string> => {
    return new Promise<string>(resolve => {
      closingRef.current = true;

      const ws = wsRef.current;
      const recorder = recorderRef.current;

      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch {
          // ignore
        }
      }

      let done = false;
      const finish = () => {
        if (done) {
          return;
        }
        done = true;
        teardown();
        setStatus('idle');
        setInterimSegments([]);
        // Persist as speaker-labeled lines. These numbered speakers are kept
        // as-is and fed to the subjective-note generation prompt downstream.
        const labeled = segmentsRef.current
          .map(s => `Speaker ${s.speaker + 1}: ${s.text.trim()}`)
          .join('\n');
        resolve(labeled.trim());
      };

      if (ws && ws.readyState === WebSocket.OPEN) {
        // CloseStream tells Deepgram to transcribe remaining audio and close.
        ws.addEventListener('close', finish, { once: true });
        try {
          ws.send(JSON.stringify({ type: 'CloseStream' }));
        } catch {
          finish();
          return;
        }
        // Safety net if the close event never arrives.
        setTimeout(finish, 2500);
      } else {
        finish();
      }
    });
  }, [teardown]);

  return {
    status,
    segments,
    interimSegments,
    isSupported,
    connect,
    pause,
    resume,
    stop,
    reset,
  };
};
